import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { 
  Search, 
  Filter, 
  Plus, 
  Edit, 
  Trash2,
  X,
  Loader,
  Calendar,
  Package,
  DollarSign
} from 'lucide-react';
import { calculateOrderItemTotal, formatPrice } from '../utils/pricingCalculations';
import {
  selectRecurringOrders,
  selectRecurringOrdersFilters,
  selectRecurringOrdersLoading,
  selectRecurringOrdersError,
  setFilters,
  clearFilters,
  fetchRecurringOrders,
  createRecurringOrder,
  updateRecurringOrder,
  deleteRecurringOrder
} from '../store/slices/recurringOrdersSlice';
import {
  selectSeason
} from '../store/slices/seasonSlice';
import RecurringOrderModal from '../components/Orders/RecurringOrderModal';
import RecurringOrderDetailModal from '../components/Orders/RecurringOrderDetailModal';
import DeleteRecurringOrderModal from '../components/Orders/DeleteRecurringOrderModal';
import { Can } from '../components/Auth/Can';

const RecurringOrders = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const orders = useSelector(selectRecurringOrders);
  const filters = useSelector(selectRecurringOrdersFilters);
  const loading = useSelector(selectRecurringOrdersLoading);
  const error = useSelector(selectRecurringOrdersError);
  const selectedSeason = useSelector(selectSeason);
  const [showFilters, setShowFilters] = useState(false);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [productos, setProductos] = useState([]);

  const { search, tipo, cliente_id } = filters;

  // Cargar clientes y productos al montar el componente
  useEffect(() => {
    const loadData = async () => {
      try {
        const { clientesAPI, productosAPI } = await import('../services/api');
        
        const clientesResponse = await clientesAPI.getAll();
        const productosResponse = await productosAPI.getAll();
        
        setClientes(clientesResponse.data || []);
        setProductos(productosResponse.data || []);
      } catch (err) {
        console.error('Error cargando datos:', err);
        setClientes([]);
        setProductos([]);
      }
    };
    
    loadData();
  }, []);

  useEffect(() => {
    if (!selectedSeason) return;

    const appliedFilters = {
      search,
      tipo,
      cliente_id,
      temporada: selectedSeason.year,
    };

    dispatch(fetchRecurringOrders(appliedFilters));
  }, [dispatch, selectedSeason, search, tipo, cliente_id]);

  const handleFilterChange = (key, value) => {
    dispatch(setFilters({ [key]: value }));
  };

  const handleClearFilters = () => {
    dispatch(clearFilters());
    setShowFilters(false);
  };

  const handleCreateOrder = () => {
    setSelectedOrder(null);
    setShowOrderModal(true);
  };

  const handleEditOrder = (order) => {
    setSelectedOrder(order);
    setShowOrderModal(true);
  };

  const handleDeleteOrder = (order) => {
    setSelectedOrder(order);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async (orderId) => {
    await dispatch(deleteRecurringOrder(orderId));
    // Recargar pedidos
    if (selectedSeason) {
      dispatch(fetchRecurringOrders({
        temporada: selectedSeason.year,
        search,
        tipo,
        cliente_id
      }));
    }
  };

  const handleSaveOrder = async (orderData) => {
    try {
      if (selectedOrder) {
        await dispatch(updateRecurringOrder({ id: selectedOrder.id, orderData }));
      } else {
        await dispatch(createRecurringOrder(orderData));
      }
      setShowOrderModal(false);
      setSelectedOrder(null);
      // Recargar pedidos
      if (selectedSeason) {
        dispatch(fetchRecurringOrders({
          temporada: selectedSeason.year,
          search,
          tipo,
          cliente_id
        }));
      }
    } catch (err) {
      console.error('Error al guardar pedido:', err);
    }
  };

  const getTypeBadgeClass = (tipo) => {
    return tipo === 'Exportación' ? 'badge badge-primary' : 'badge badge-success';
  };

  const calculateTotals = (order) => {
    const detalles = order.detalles || [];
    
    // Exclude Despacho products from box count
    const totalCajas = detalles.reduce((sum, d) => {
      const producto = productos.find(p => p.id === d.producto_id);
      if (producto?.category === 'Despacho') {
        return sum; // Skip Despacho products
      }
      return sum + Number(d.cantidad_cajas || 0);
    }, 0);
    
    // Sumar directamente el precio_total guardado en la BD
    const totalAmount = detalles.reduce((sum, d) => {
      return sum + Number(d.precio_total || 0);
    }, 0);
    
    return { totalCajas, totalAmount };
  };

  const calculateSummaryTotals = () => {
    let totalExportacionUSD = 0;
    let totalNacionalCLP = 0;
    
    orders.forEach(order => {
      const { totalAmount } = calculateTotals(order);
      if (order.tipo === 'Exportación') {
        totalExportacionUSD += totalAmount;
      } else if (order.tipo === 'Nacional') {
        totalNacionalCLP += totalAmount;
      }
    });
    
    return { totalExportacionUSD, totalNacionalCLP };
  };

  return (
    <div className="orders-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Pedidos Recurrentes
          </h1>
          <p className="text-lg text-gray-600 mt-2">
            Gestión de pedidos que se repiten semanalmente
          </p>
        </div>
        <Can I="create" this="orders:recurring">
          <button className="btn btn-primary" onClick={handleCreateOrder}>
            <Plus size={20} />
            Nuevo Pedido Recurrente
          </button>
        </Can>
      </div>

      {/* Summary Card */}
      <div className="card">
        <div className="week-summary">
          <h3 className="summary-title">Totales</h3>
          <div className="summary-stats">
            <div className="summary-stat">
              <div className="stat-icon bg-primary">
                <Package size={20} />
              </div>
              <div>
                <p className="stat-label">Pedidos Recurrentes</p>
                <p className="stat-value">{orders.length}</p>
              </div>
            </div>

            <div className="summary-stat">
              <div className="stat-icon bg-secondary">
                <DollarSign size={20} />
              </div>
              <div>
                <p className="stat-label">USD</p>
                <p className="stat-value">
                  {formatPrice(calculateSummaryTotals().totalExportacionUSD, 'USD')}
                </p>
              </div>
            </div>

            <div className="summary-stat">
              <div className="stat-icon bg-primary">
                <DollarSign size={20} />
              </div>
              <div>
                <p className="stat-label">CLP</p>
                <p className="stat-value">
                  {formatPrice(calculateSummaryTotals().totalNacionalCLP, 'CLP')}
                </p>
              </div>
            </div>

            <div className="summary-stat">
              <div className="stat-icon bg-accent">
                <Calendar size={20} />
              </div>
              <div>
                <p className="stat-label">Temporada</p>
                <p className="stat-value">{selectedSeason?.year || '-'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="card">
        <div className="filters-section">
          <div className="search-bar">
            <Search size={20} />
            <input
              type="text"
              placeholder="Buscar pedidos recurrentes..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="search-input"
            />
          </div>
          
          <div className="filter-actions">
            <button 
              className={`btn btn-outline ${showFilters ? 'active' : ''}`}
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter size={20} />
              Filtros
            </button>
            
            {(filters.search || filters.tipo !== 'all' || filters.cliente_id !== 'all') && (
              <button 
                className="btn btn-outline"
                onClick={handleClearFilters}
              >
                <X size={20} />
                Limpiar
              </button>
            )}
          </div>
        </div>

        {/* Advanced Filters */}
        {showFilters && (
          <div className="advanced-filters">
            <div className="filter-grid">
              <div className="form-group">
                <label className="form-label">Tipo</label>
                <select
                  className="form-select"
                  value={filters.tipo}
                  onChange={(e) => handleFilterChange('tipo', e.target.value)}
                >
                  <option value="all">Todos</option>
                  <option value="Exportación">Exportación</option>
                  <option value="Nacional">Nacional</option>
                </select>
              </div>
              
              <div className="form-group">
                <label className="form-label">Cliente</label>
                <select
                  className="form-select"
                  value={filters.cliente_id}
                  onChange={(e) => handleFilterChange('cliente_id', e.target.value)}
                >
                  <option value="all">Todos</option>
                  {clientes.map(cliente => (
                    <option key={cliente.id} value={cliente.id}>
                      {cliente.nombre}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Results Summary */}
      <div className="results-summary">
        <p className="text-gray-600">
          {selectedSeason
            ? `Mostrando ${orders.length} pedido${orders.length !== 1 ? 's' : ''} recurrente${orders.length !== 1 ? 's' : ''} para la temporada ${selectedSeason.year}`
            : 'Selecciona una temporada para visualizar los pedidos recurrentes.'}
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          <div className="alert alert-error">
            <span>Error: {error}</span>
            <button 
              className="btn btn-sm btn-outline"
              onClick={() => {
                if (!selectedSeason) return;
                dispatch(fetchRecurringOrders({
                  temporada: selectedSeason.year,
                  search,
                  tipo,
                  cliente_id
                }));
              }}
            >
              Reintentar
            </button>
          </div>
        </div>
      )}

      {/* Orders Table */}
      <div className="card">
        {loading ? (
          <div className="loading-state">
            <Loader className="animate-spin" size={32} />
            <p className="text-gray-600 mt-4">Cargando pedidos recurrentes...</p>
          </div>
        ) : orders.length > 0 ? (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Tipo</th>
                  <th>Rango de Semanas</th>
                  <th>Total Cajas</th>
                  <th>Total</th>
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => {
                  const totals = calculateTotals(order);
                  return (
                    <tr key={order.id}>
                      <td>
                        <div className="customer-info">
                          <div className="font-medium">{order.clientes?.nombre || 'Sin cliente'}</div>
                          <div className="text-sm text-gray-500">
                            {order.clientes?.pais} - {order.clientes?.ciudad}
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className={getTypeBadgeClass(order.tipo)}>
                          {order.tipo}
                        </span>
                      </td>
                      <td>
                        <div className="delivery-info">
                          <div className="flex items-center gap-1">
                            <Calendar size={14} />
                            <span className="font-medium">
                              Semana {order.semana_inicio} - {order.semana_fin}
                            </span>
                          </div>
                          <div className="text-sm text-gray-500">
                            {order.semana_fin - order.semana_inicio + 1} semanas
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="font-medium">{totals.totalCajas}</div>
                      </td>
                      <td>
                        <div className="price-info">
                          <div className="flex items-center gap-1">
                            <span className="font-medium text-lg">
                              {formatPrice(totals.totalAmount, order.tipo === 'Exportación' ? 'USD' : 'CLP')}
                            </span>
                          </div>
                          <div className="text-sm">
                            <span className={order.tipo === 'Exportación' ? 'badge badge-primary' : 'badge badge-success'}>
                              {order.tipo === 'Exportación' ? 'USD' : 'CLP'}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <Can I="update" this="orders:recurring">
                            <button 
                              className="action-btn edit"
                              onClick={() => handleEditOrder(order)}
                              title="Editar"
                            >
                              <Edit size={16} />
                            </button>
                          </Can>
                          <Can I="delete" this="orders:recurring">
                            <button 
                              className="action-btn delete"
                              onClick={() => handleDeleteOrder(order)}
                              title="Eliminar"
                            >
                              <Trash2 size={16} />
                            </button>
                          </Can>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-state">
            <div className="empty-icon">📋</div>
            <h3 className="text-lg font-medium text-gray-900 mt-4">
              No hay pedidos recurrentes
            </h3>
            <p className="text-gray-500 mt-2">
              Los pedidos recurrentes creados aparecerán aquí
            </p>
          </div>
        )}
      </div>

      {/* Order Modal */}
      <RecurringOrderModal
        isOpen={showOrderModal}
        onClose={() => {
          setShowOrderModal(false);
          setSelectedOrder(null);
        }}
        onSave={handleSaveOrder}
        order={selectedOrder}
        clientes={clientes}
        productos={productos}
        temporada={selectedSeason}
      />

      {/* Order Detail Modal */}
      <RecurringOrderDetailModal
        isOpen={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        order={selectedOrder}
        productos={productos}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedOrder && (
        <DeleteRecurringOrderModal
          order={selectedOrder}
          onClose={() => {
            setShowDeleteModal(false);
            setSelectedOrder(null);
          }}
          onDelete={handleConfirmDelete}
        />
      )}

      <style jsx>{`
        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: var(--spacing-6);
        }
        
        .week-navigation {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: var(--spacing-6);
        }
        
        .week-controls {
          display: flex;
          align-items: center;
          gap: var(--spacing-4);
        }
        
        .week-nav-btn {
          padding: var(--spacing-2);
          border: 2px solid var(--gray-300);
          border-radius: var(--radius-md);
          background: white;
          cursor: pointer;
          transition: all 0.2s ease-in-out;
        }
        
        .week-nav-btn:hover {
          border-color: var(--primary-blue);
          background-color: var(--primary-blue);
          color: white;
        }
        
        .week-info {
          text-align: center;
          min-width: 200px;
        }
        
        .week-title {
          font-size: var(--font-size-lg);
          font-weight: 600;
          color: var(--gray-900);
          margin: 0;
        }
        
        .week-dates {
          font-size: var(--font-size-sm);
          color: var(--gray-600);
          margin: var(--spacing-1) 0 0 0;
        }
        
        .week-summary {
          padding-top: var(--spacing-6);
        }
        
        .summary-stats {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: var(--spacing-4);
        }
        
        .summary-stat {
          display: flex;
          align-items: center;
          gap: var(--spacing-3);
          padding: var(--spacing-4);
          background-color: var(--gray-50);
          border-radius: var(--radius-lg);
        }
        
        .stat-icon {
          width: 40px;
          height: 40px;
          border-radius: var(--radius-lg);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }
        
        .stat-label {
          font-size: var(--font-size-sm);
          color: var(--gray-600);
          margin: 0;
        }
        
        .stat-value {
          font-size: var(--font-size-xl);
          font-weight: 700;
          color: var(--gray-900);
          margin: 0;
        }
        
        .filters-section {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: var(--spacing-4);
          margin-bottom: var(--spacing-4);
        }
        
        .search-bar {
          position: relative;
          flex: 1;
          max-width: 400px;
        }
        
        .search-bar svg {
          position: absolute;
          left: var(--spacing-3);
          top: 50%;
          transform: translateY(-50%);
          color: var(--gray-400);
        }
        
        .search-input {
          width: 100%;
          padding: var(--spacing-3) var(--spacing-3) var(--spacing-3) var(--spacing-10);
          border: 2px solid var(--gray-300);
          border-radius: var(--radius-md);
          font-size: var(--font-size-base);
          transition: border-color 0.2s ease-in-out;
        }
        
        .search-input:focus {
          outline: none;
          border-color: var(--primary-blue);
          box-shadow: 0 0 0 3px rgb(59 130 246 / 0.1);
        }
        
        .filter-actions {
          display: flex;
          gap: var(--spacing-2);
        }
        
        .btn.active {
          background-color: var(--primary-blue);
          color: white;
        }
        
        .advanced-filters {
          padding-top: var(--spacing-4);
          border-top: 1px solid var(--gray-200);
        }
        
        .filter-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: var(--spacing-4);
        }
        
        .results-summary {
          margin-bottom: var(--spacing-4);
        }
        
        .table-container {
          overflow-x: auto;
        }
        
        .order-info {
          display: flex;
          align-items: center;
          gap: var(--spacing-3);
        }
        
        .order-icon {
          width: 40px;
          height: 40px;
          background-color: var(--gray-100);
          border-radius: var(--radius-lg);
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--gray-600);
        }
        
        .customer-info {
          display: flex;
          flex-direction: column;
          gap: var(--spacing-1);
        }
        
        .delivery-info {
          display: flex;
          flex-direction: column;
          gap: var(--spacing-1);
        }
        
        .price-info {
          display: flex;
          flex-direction: column;
          gap: var(--spacing-1);
        }
        
        .action-buttons {
          display: flex;
          gap: var(--spacing-2);
        }
        
        .action-btn {
          padding: var(--spacing-2);
          border: none;
          border-radius: var(--radius-md);
          cursor: pointer;
          transition: all 0.2s ease-in-out;
        }
        
        .action-btn.view {
          background-color: var(--gray-100);
          color: var(--gray-600);
        }
        
        .action-btn.view:hover {
          background-color: var(--secondary-green);
          color: white;
        }
        
        .action-btn.edit {
          background-color: var(--gray-100);
          color: var(--gray-600);
        }
        
        .action-btn.edit:hover {
          background-color: var(--primary-blue);
          color: white;
        }
        
        .action-btn.delete {
          background-color: var(--gray-100);
          color: var(--gray-600);
        }
        
        .action-btn.delete:hover {
          background-color: var(--error);
          color: white;
        }
        
        .empty-state {
          text-align: center;
          padding: var(--spacing-12) var(--spacing-6);
        }
        
        .empty-icon {
          font-size: 4rem;
          margin-bottom: var(--spacing-4);
        }
        
        .loading-state {
          text-align: center;
          padding: var(--spacing-12) var(--spacing-6);
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        
        .error-message {
          margin-bottom: var(--spacing-4);
        }
        
        .alert {
          padding: var(--spacing-4);
          border-radius: var(--radius-md);
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        
        .alert-error {
          background-color: #fef2f2;
          border: 1px solid #fecaca;
          color: #dc2626;
        }
        
        .animate-spin {
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        
        @media (max-width: 768px) {
          .page-header {
            flex-direction: column;
            gap: var(--spacing-4);
          }
          
          .week-navigation {
            flex-direction: column;
            gap: var(--spacing-4);
          }
          
          .week-controls {
            justify-content: center;
          }
          
          .summary-stats {
            grid-template-columns: repeat(2, 1fr);
          }
          
          .filters-section {
            flex-direction: column;
            align-items: stretch;
          }
          
          .search-bar {
            max-width: none;
          }
          
          .filter-grid {
            grid-template-columns: 1fr;
          }
          
          .table {
            font-size: var(--font-size-sm);
          }
          
          .order-info {
            flex-direction: column;
            align-items: flex-start;
            gap: var(--spacing-2);
          }
          
          .order-icon {
            width: 32px;
            height: 32px;
          }
        }
      `}</style>
    </div>
  );
};

export default RecurringOrders;
